import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createSelectors } from '~/src/lib/createSelectors'

export interface DupeInputState {
  // Core states
  freeProSearchesRemaining: number
  totalProSearchesUsed: number
  hasUsedAllFreeSearches: boolean
  lastResetDate: string // Store as ISO string
  initialFreeProSearches: number

  // Search feature states
  proSearch: boolean

  // Actions
  decrementFreeProSearches: () => void
  checkAndResetDaily: () => void
  reset: () => void

  // Search toggle actions
  toggleProSearch: (enabled?: boolean) => void
}

const INITIAL_FREE_PRO_SEARCHES = 5

const isSameDay = (date1: Date, date2: Date) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

// Create a single store instance with initialization check
const initializeStore = (preloadedState: Partial<DupeInputState> = {}) =>
  create<DupeInputState>()(
    persist(
      (set, get) => ({
        // Initial states
        initialFreeProSearches: INITIAL_FREE_PRO_SEARCHES,
        freeProSearchesRemaining: INITIAL_FREE_PRO_SEARCHES,
        totalProSearchesUsed: 0,
        hasUsedAllFreeSearches: false,
        lastResetDate: new Date().toISOString(),
        proSearch: false,
        ...preloadedState,

        // Actions
        checkAndResetDaily: () =>
          set((state) => {
            const today = new Date()
            const lastReset = new Date(state.lastResetDate)

            if (!isSameDay(today, lastReset)) {
              // Only update state if we actually need to reset
              if (
                state.freeProSearchesRemaining !==
                  state.initialFreeProSearches ||
                state.hasUsedAllFreeSearches
              ) {
                return {
                  freeProSearchesRemaining: state.initialFreeProSearches,
                  hasUsedAllFreeSearches: false,
                  lastResetDate: today.toISOString(),
                }
              }
            }
            return state
          }),

        decrementFreeProSearches: () =>
          set((state) => {
            // First check if we need to reset for the day
            get().checkAndResetDaily()

            const remaining = Math.max(0, state.freeProSearchesRemaining - 1)
            return {
              freeProSearchesRemaining: remaining,
              totalProSearchesUsed: state.totalProSearchesUsed + 1,
              hasUsedAllFreeSearches: remaining === 0,
            }
          }),

        reset: () =>
          set((state) => ({
            freeProSearchesRemaining: state.initialFreeProSearches,
            totalProSearchesUsed: 0,
            hasUsedAllFreeSearches: false,
            lastResetDate: new Date().toISOString(),
            proSearch: false,
          })),

        toggleProSearch: (enabled?: boolean) =>
          set((state) => ({ proSearch: enabled ?? !state.proSearch })),
      }),
      {
        name: 'dupe-input-storage',
        storage: {
          getItem: (name) => {
            if (typeof window === 'undefined') return null

            const str = localStorage.getItem(name)
            if (!str) return null

            try {
              const { state } = JSON.parse(str)
              // Only set defaults for missing values, don't override existing ones
              return {
                state: {
                  initialFreeProSearches: INITIAL_FREE_PRO_SEARCHES,
                  freeProSearchesRemaining: INITIAL_FREE_PRO_SEARCHES,
                  totalProSearchesUsed: 0,
                  hasUsedAllFreeSearches: false,
                  lastResetDate: new Date().toISOString(),
                  proSearch: false,
                  ...state, // Apply stored values last to prevent default overwrites
                },
              }
            } catch (error) {
              console.error('Error parsing dupeInputStore:', error)
              return null
            }
          },
          setItem: (name, value) => {
            if (typeof window === 'undefined') return

            try {
              localStorage.setItem(name, JSON.stringify(value))
            } catch (error) {
              console.error('Error saving dupeInputStore:', error)
            }
          },
          removeItem: (name) => {
            if (typeof window === 'undefined') return
            localStorage.removeItem(name)
          },
        },
      }
    )
  )

// Create a single store instance with initialization check
let store: ReturnType<typeof initializeStore> | undefined

const getStore = () => {
  if (typeof window === 'undefined') {
    // Always return a new store instance during SSR
    return initializeStore()
  }

  // Initialize store only once in client
  if (!store) {
    store = initializeStore()
  }
  return store
}

// Export the store hooks
export const useDupeInputStore = createSelectors(getStore())

// Action groups for common operations
export const useStoreActions = () => {
  const store = useDupeInputStore()

  return {
    toggleProSearch: store.toggleProSearch,
    decrementFreeProSearches: store.decrementFreeProSearches,
    checkAndResetDaily: store.checkAndResetDaily,
  }
}

// Selectors
export const useProSearch = () => useDupeInputStore((state) => state.proSearch)

export const useFreeProSearchesRemaining = () => {
  const store = useDupeInputStore()
  // Check for reset before returning the value
  store.checkAndResetDaily()
  return store.freeProSearchesRemaining
}

export const useHasUsedAllFreeSearches = () => {
  const store = useDupeInputStore()
  // Check for reset before returning the value
  store.checkAndResetDaily()
  return store.hasUsedAllFreeSearches
}

export const useTotalProSearchesUsed = () =>
  useDupeInputStore((state) => state.totalProSearchesUsed)

export const useInitialFreeProSearches = () =>
  useDupeInputStore((state) => state.initialFreeProSearches)
